/* eslint-disable max-lines */
import { useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner";
import { SubmissionCard } from "../../../shared/components/SubmissionCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../shared/components/ui/alert-dialog";
import { Button } from "../../../shared/components/ui/button";
import { useCascadeFadeIn } from "../../../shared/hooks/useCascadeFadeIn";
import { useSubmissionMutations } from "../../content-editing/hooks/useSubmissionMutations";
import { useSubmissions, type Submission, type SubmissionStatus } from "../../content-editing/hooks/useSubmissions";

/** Number of submissions displayed per page */
const PAGE_SIZE = 20;

/** Status filter configuration */
const STATUS_FILTERS = [
  { label: "All", value: undefined },
  { label: "Draft", value: "DRAFT" },
  { label: "Pending Review", value: "PENDING" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Published", value: "PUBLISHED" },
] as const satisfies ReadonlyArray<{ label: string; value: SubmissionStatus | undefined }>;

/**
 * Calculate pagination range for display
 */
function getPaginationRange(page: number, total: number): { start: number; end: number } {
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);
  return { start, end };
}

/**
 * Provider Submissions Dashboard
 * Lists all submissions by the current provider with filtering and status tracking
 */
/* eslint-disable max-lines-per-function */
// NOTE: Dashboard component with multiple conditional renders (loading, error, empty, list states)
export function SubmissionsDashboard(): ReactElement {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<SubmissionStatus | undefined>(undefined);
  const [page, setPage] = useState(1);
  const { data, loading, error, refetch } = useSubmissions(selectedStatus, page, PAGE_SIZE);
  const { deleteSubmission, loading: deletingLoading } = useSubmissionMutations();

  const handleDelete = async (id: string) => {
    try {
      await deleteSubmission(id);
      // Refetch the list after deletion
      refetch();
    } catch (err) {
      console.error("Failed to delete submission:", err);
      alert("Failed to delete submission. Please try again.");
    }
  };

  return (
    <div className="space-y-8 pt-8">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-white mb-2">Submission List</h1>
        <p className="text-sm text-neutral-400">Manage your content submissions and track their status</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.label}
            onClick={() => {
              setSelectedStatus(filter.value);
              setPage(1);
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              selectedStatus === filter.value
                ? "text-white border-b-2 border-lumi-accent"
                : "text-neutral-400 hover:text-white"
            }`}
          >
            {filter.label}
          </button>
        ))}
        <Button className="ml-auto" variant="ghost-sm" onClick={() => navigate("/provider/new")}>
          + New Submission
        </Button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="border border-neutral-800 rounded-lg p-12 text-center">
          <LoadingSpinner className="mx-auto" />
          <p className="mt-4 text-neutral-400">Loading submissions...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-center">
          <p className="text-red-400">Error loading submissions: {error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && (!data || data.submissions.length === 0) && (
        <div className="bg-none border border-neutral-800 rounded-lg p-12 text-center">
          <div className="max-w-md mx-auto space-y-4">
            <div className="w-16 h-16 bg-neutral-800 rounded-full mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white">No submissions yet</h3>
            <p className="text-sm text-neutral-500 py-2">
              Get started by creating your first content submission. We'll guide you through the process step by step.
            </p>
            <Button variant="outline" onClick={() => navigate("/provider/new")} className="mt-4">
              Create Your First Submission
            </Button>
          </div>
        </div>
      )}

      {/* Submissions List */}
      {!loading && !error && data && data.submissions.length > 0 && (
        <SubmissionsList
          submissions={data.submissions}
          deletingLoading={deletingLoading}
          onEdit={(id) => navigate(`/provider/edit/${id}`)}
          onView={(id) => navigate(`/provider/view/${id}`)}
          onDelete={handleDelete}
        />
      )}

      {/* Pagination */}
      {!loading && data && data.total > 0 && (
        <div className="flex items-center justify-between pt-4">
          <p className="text-sm text-neutral-400">
            Showing {getPaginationRange(page, data.total).start}-{getPaginationRange(page, data.total).end} of{" "}
            {data.total} submissions
          </p>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1 bg-neutral-800 text-white rounded text-sm hover:bg-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              disabled={page * PAGE_SIZE >= data.total}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1 bg-neutral-800 text-white rounded text-sm hover:bg-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Submissions List with cascade fade-in animation
 */
interface SubmissionsListProps {
  readonly submissions: readonly Submission[];
  readonly deletingLoading: boolean;
  readonly onEdit: (id: string) => void;
  readonly onView: (id: string) => void;
  readonly onDelete: (id: string) => Promise<void>;
}

function SubmissionsList({
  submissions,
  deletingLoading,
  onEdit,
  onView,
  onDelete,
}: SubmissionsListProps): ReactElement {
  return (
    <div className="space-y-4">
      {submissions.map((submission, index) => (
        <SubmissionListItem
          key={submission.id}
          submission={submission}
          index={index}
          deletingLoading={deletingLoading}
          onEdit={onEdit}
          onView={onView}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

/**
 * Individual submission item with fade-in animation
 */
interface SubmissionListItemProps {
  readonly submission: Submission;
  readonly index: number;
  readonly deletingLoading: boolean;
  readonly onEdit: (id: string) => void;
  readonly onView: (id: string) => void;
  readonly onDelete: (id: string) => Promise<void>;
}

function SubmissionListItem({
  submission,
  index,
  deletingLoading,
  onEdit,
  onView,
  onDelete,
}: SubmissionListItemProps): ReactElement {
  const { ref, isVisible } = useCascadeFadeIn({ index });

  // Determine status-specific actions
  const actions = getSubmissionActions(submission, deletingLoading, onEdit, onView, onDelete);

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ease-out ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
    >
      <SubmissionCard submission={submission} actions={actions} />
    </div>
  );
}

/**
 * Get actions for a submission based on its status
 */
function getSubmissionActions(
  submission: Submission,
  deletingLoading: boolean,
  onEdit: (id: string) => void,
  onView: (id: string) => void,
  onDelete: (id: string) => Promise<void>,
): ReactElement {
  if (submission.status === "DRAFT") {
    return (
      <>
        <Button
          variant="outline"
          onClick={() => {
            if ("startViewTransition" in document) {
              (document as Document & { startViewTransition: (callback: () => void) => void }).startViewTransition(
                () => {
                  onEdit(submission.id);
                },
              );
            } else {
              onEdit(submission.id);
            }
          }}
          className="text-sm sm:w-full"
        >
          Edit
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button disabled={deletingLoading} className="btn-danger sm:w-full">
              <span className="relative z-10">Delete</span>
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Submission</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{submission.title}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  void onDelete(submission.id);
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  if (submission.status === "REJECTED") {
    return (
      <Button
        variant="compact"
        onClick={() => {
          if ("startViewTransition" in document) {
            (document as Document & { startViewTransition: (callback: () => void) => void }).startViewTransition(() => {
              onEdit(submission.id);
            });
          } else {
            onEdit(submission.id);
          }
        }}
        className="text-sm bg-orange-600 hover:bg-orange-700 sm:w-full"
      >
        Edit & Resubmit
      </Button>
    );
  }

  // PENDING, APPROVED, PUBLISHED
  return (
    <Button
      variant="ghost-sm"
      onClick={() => {
        if ("startViewTransition" in document) {
          (document as Document & { startViewTransition: (callback: () => void) => void }).startViewTransition(() => {
            onView(submission.id);
          });
        } else {
          onView(submission.id);
        }
      }}
      className="text-sm sm:w-full"
    >
      View Details
    </Button>
  );
}
